// Frontend/src/contexts/MinistryContext.js
import { createContext, useContext, useEffect, useState } from 'react';
import ministryService from '../services/ministryService';

const MinistryContext = createContext();

export const useMinistries = () => {
  const context = useContext(MinistryContext);
  if (!context) {
    throw new Error('useMinistries must be used within a MinistryProvider');
  }
  return context;
};

export const MinistryProvider = ({ children }) => {
  const [ministries, setMinistries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch all ministries from the backend
  const fetchMinistries = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const data = await ministryService.getAllMinistries();
      setMinistries(Array.isArray(data) ? data : data.ministries || []);
      
    } catch (error) {
      console.error('Error fetching ministries:', error);
      setError({
        message: error.response?.data?.message || 'Failed to load ministries. Please try again.'
      });
      setMinistries([]);
    } finally {
      setLoading(false);
    }
  };

  // Get a specific ministry by ID
  const getMinistryById = async (id) => {
    try {
      const ministry = await ministryService.getMinistryById(id);
      return ministry;
    } catch (error) {
      console.error('Error fetching ministry:', error);
      throw error;
    }
  };

  // Get a ministry by name
  const getMinistryByName = async (name) => {
    try {
      const ministry = await ministryService.getMinistryByName(name);
      return ministry;
    } catch (error) {
      console.error('Error fetching ministry by name:', error);
      throw error;
    }
  };

  // Create a new ministry
  const createMinistry = async (ministryData) => {
    try {
      const newMinistry = await ministryService.createMinistry(ministryData);
      setMinistries(prev => [...prev, newMinistry]);
      return newMinistry;
    } catch (error) {
      console.error('Error creating ministry:', error);
      throw error;
    }
  };

  // Update an existing ministry
  const updateMinistry = async (id, ministryData) => {
    try {
      const updatedMinistry = await ministryService.updateMinistry(id, ministryData);
      setMinistries(prev => 
        prev.map(ministry => 
          ministry.id === id ? updatedMinistry : ministry
        )
      );
      return updatedMinistry;
    } catch (error) {
      console.error('Error updating ministry:', error);
      throw error;
    }
  };

  // Delete a ministry
  const deleteMinistry = async (id) => {
    try {
      await ministryService.deleteMinistry(id);
      setMinistries(prev => prev.filter(ministry => ministry.id !== id));
      return true;
    } catch (error) {
      console.error('Error deleting ministry:', error);
      throw error;
    }
  };

  // Refresh ministries data
  const refreshMinistries = () => {
    fetchMinistries();
  };

  // Load ministries on component mount
  useEffect(() => {
    fetchMinistries();
  }, []);

  const contextValue = {
    // State
    ministries,
    loading,
    error,
    
    // State setters (for direct manipulation when needed)
    setMinistries,
    setLoading,
    setError,
    
    // API methods
    fetchMinistries: refreshMinistries,
    getMinistryById,
    getMinistryByName,
    createMinistry,
    updateMinistry,
    deleteMinistry,
    refreshMinistries,
    
    // Computed values
    activeMinistries: ministries.filter(ministry => ministry.status === 'active'),
    inactiveMinistries: ministries.filter(ministry => ministry.status === 'inactive'),
    pendingMinistries: ministries.filter(ministry => ministry.status === 'pending'),
    totalMinistries: ministries.length
  };

  return (
    <MinistryContext.Provider value={contextValue}>
      {children}
    </MinistryContext.Provider>
  );
};